import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import CarrierService from '../services/CarrierService';
import ICarrierData from '../types/Carriers';

/**
 * Custom hook to fetch carrier data from the API
 * @returns An object containing the carrier data and loading state
 */
export const useCarrierData = () => {
  const [carriers, setCarriers] = useState<ICarrierData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();

  useEffect(() => {
    const fetchCarriers = async () => {
      try {
        setIsLoading(true);
        const token = auth.user?.access_token;
        
        if (!token) {
          throw new Error('Authentication token not available');
        }
        
        const response = await CarrierService.getAll(token);
        setCarriers(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching carrier data:', err);
        setError('Failed to load carrier data. Please try again.');
        setCarriers([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (auth.isAuthenticated) {
      fetchCarriers();
    }
  }, [auth]);

  /**
   * Get carrier ID by carrier code
   * @param carrierCode - The carrier code to look up
   * @returns The carrier ID or undefined if not found
   */
  const getCarrierIdByCode = (carrierCode: string): string | number | undefined => {
    // Defensive check in case carrierCode is undefined
    if (!carrierCode) {
      console.warn('Cannot get carrier ID: Carrier code is undefined or empty');
      return undefined;
    }

    // Since we've already cleaned the data when fetching, we can do a direct comparison
    // Still trim the input carrier code though, just to be safe
    const trimmedCarrierCode = carrierCode.trim();

    // Find the carrier with the matching code
    // Trim trailing spaces from carrier codes in the data for comparison
    const carrier = carriers.find(c => c.carrierCode.trim() === trimmedCarrierCode);
    const carrierId = carrier?.carrierId;

    return carrierId;
  };

  /**
   * Get carrier code by carrier ID
   * @param carrierId - The carrier Id to look up
   * @returns The carrier code or undefined if not found
   */
  const getCarrierCodeById = (carrierId: number): string | undefined => {
    // Defensive check in case carrierId is undefined
    if (!carrierId) {
      console.warn('Cannot get carrier code: Carrier Id is undefined or empty');
      return undefined;
    }

    // Find the carrier with the matching Id
    const carrier = carriers.find(c => c.carrierId === carrierId);
    const carrierCode = carrier?.carrierCode;

    return carrierCode;
  };

  /**
   * Get carrier data by carrier code
   * @param carrierCode - The carrier code to look up
   * @returns The carrier data or undefined if not found
   */
  const getCarrierByCode = (carrierCode: string): ICarrierData | undefined => {
    if (!carrierCode) return undefined;

    // Trim the input carrier code
    const trimmedCarrierCode = carrierCode.trim();

    // Directly compare with the cleaned carrier code
    return carriers.find(c => c.carrierCode === trimmedCarrierCode);
  };

  /**
   * Get carrier name by carrier code
   * @param carrierCode - The carrier code to look up
   * @returns The carrier name or empty string if not found
   */
  const getCarrierNameByCode = (carrierCode: string): string => {
    if (!carrierCode) return '';

    // Trim the input carrier code
    const trimmedCarrierCode = carrierCode.trim();

    // Directly compare with the cleaned carrier code
    const carrier = carriers.find(c => c.carrierCode === trimmedCarrierCode);

    // Since we've already cleaned the carrier name during fetch, we can return it directly
    return carrier?.carrierName || '';
  };

  // Format carriers for dropdown options
  const carrierOptions = carriers.map(carrier => {
    const code = carrier.carrierCode.trim(); // Trim whitespace from carrier code
    return {
      value: code, // used for selection value
      label: code, // used for UI display after selection
      title: `${code} - ${carrier.carrierName.trim()}` // what shows in the dropdown list
    };
  });

  return {
    carriers,
    carrierOptions,
    isLoading,
    error,
    getCarrierIdByCode,
    getCarrierByCode,
    getCarrierNameByCode,
    getCarrierCodeById
  };
};
